import { ACCESS_TOKEN_KEY, APP_USER_ID, USER_ROLE } from './auth/constants';
import config from './config';
import { Link } from 'react-router-dom';

export const TABLET_BREAKPOINT = 767;
export const DESKTOP_BREAKPOINT = 1024;
export const SERVICE_WORKER_CHANNEL_NAME = 'service-worker-channel';

export const ISSUE_LANG_KEY = 'issue-lang';

export const FAVORITE_KEY = 'favorites';
export const BUILD_NUMBER = 'build';

export const ALERT_TIMEOUT_KEYS = {
	ID: 'alert-timeout-id',
	DATETIME: 'alert-timeout-datetime',
};

export const NAVBAR_ICON_URL =
	config.build.assetDirectory && config.build.assetDirectory !== '/'
		? `${config.build.assetDirectory}/assets/navbar-icon.png`
		: `/assets/navbar-icon.png?build=${config.build.buildNumber}`;

export const APP_ICON_URL =
	config.build.assetDirectory && config.build.assetDirectory !== '/'
		? `${config.build.assetDirectory}/assets/favicon-196.png?build=${config.build.buildNumber}`
		: `/assets/favicon-196.png?build=${config.build.buildNumber}`;

export const NavbarIconLogo = () => {
	const { website } = config.build;
	if (website) {
		return (
			// eslint-disable-next-line jsx-a11y/anchor-is-valid
			<a rel="noreferrer" href={website} target="_blank">
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</a>
		);
	}
	return (
		<Link to={'/'}>
			<img src={NAVBAR_ICON_URL} alt="Logo" />
		</Link>
	);
};

export const MANIFEST_ICON_URL = config.build.assetDirectory
	? `${
			config.build.assetDirectory === '/'
				? ''
				: config.build.assetDirectory
	  }/assets/manifest-icon-512.png?build=${config.build.buildNumber}`
	: `/assets/manifest-icon-512.png?build=${config.build.buildNumber}`;

export function reduceExcludeComponents(
	acc: Array<any>,
	component: any,
): Array<any> {
	component = clone(component);
	const { excludedchannels } = component;
	if (component.columns) {
		component.columns = component.columns.map((column: Array<any>) => {
			return column ? column.reduce(reduceExcludeComponents, []) : column;
		});
	}
	if (!excludedchannels) {
		acc.push(component);
		return acc;
	}

	const excludedChannels = new Set([...excludedchannels]);
	if (excludedChannels.has('app')) {
		return acc;
	}
	acc.push(component);
	return acc;
}

export interface Issue {
	id: string;
	name: any;
	description: string;
	index: number;
	cover: string;
	created?: any;
	features: Array<string>;
	isFree: boolean;
	releaseDate?: any;
	updated?: any;
	articles?: Array<Article>;
	featuredArticles?: Array<Article>;
	pages: any;
	slug: string;
	externalUrl: string;
}

export interface Section {
	id: string;
	name: string;
	position: number;
	issues?: Array<Issue>;
}

export interface Article {
	id: string;
	role: 'advert' | 'article';
	ArticleID?: string;
	name?: any;
	author?: null | string;
	description?: string;
	thumbnail?: string;
	featureImage?: string;
	features: Array<string>;
	index: number;
	style?: string;
	components?: Array<any>;
	created?: any;
	updated?: any;
	releaseDate?: any;
	IssueID?: string;
	issueSlug?: string;
	keywords?: Array<string>;
	audio?: string;
	pages?: Array<string>;
	channel?: any;
	slug?: string;
	isFeatured: boolean;
}

export function fixInvalidInPages(page: string) {
	if (!page.replace) {
		return page;
	}
	return page
		.replace(
			/https:\/\/cdn\.pwa\.canvasflow\.io\/https:\/\/cdn\.pwa\.canvasflow\.io/,
			'https://cdn.pwa.canvasflow.io',
		)
		.replace(
			/https:\/\/cdn\.pwa\.cflowdev\.com\/https:\/\/cdn\.pwa\.cflowdev\.com/,
			'https://cdn.pwa.cflowdev.com',
		);
}

export function getImagesFromIssue(images: Set<string>, issues: Array<Issue>) {
	for (const issue of issues) {
		const { cover, featuredArticles } = issue;
		images.add(cover);
		if (!featuredArticles) {
			continue;
		}

		for (const article of featuredArticles) {
			const { thumbnail, featureImage } = article;
			if (thumbnail) {
				images.add(thumbnail);
			}

			if (featureImage) {
				images.add(featureImage);
			}
		}
	}
}

export interface Style {
	id: string;
	name: string;
	description: string;
	properties: any;
	supportedDevices: Set<DeviceType>;
	type: string;
	parent: string | null;
	tablet: string | null;
	desktop: string | null;
	created?: any;
	lastModified?: any;
}

export interface Font {
	name: string;
	urls: Array<string>;
}

export type DeviceType = 'mobile' | 'desktop' | 'tablet';

export function getAppToken(): undefined | string | null {
	if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
		return undefined;
	}

	return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export const GET_STYLES_QUERY = `
	{
		styles {
			id
			name
			description
			properties
			supportedDevices
			type
			parent
			tablet
			desktop
			created
			lastModified
		}
	}
`;

export function clone(obj: any): any {
	if (structuredClone) {
		return structuredClone(obj);
	}

	return obj ? JSON.stringify(obj) : obj;
}

export function getAppUser(): undefined | string | null {
	const userId = localStorage.getItem(APP_USER_ID);
	if (!userId) {
		return undefined;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		return userId;
	}

	return undefined;
}

export function getAccessToken(): undefined | string | null {
	let accessToken = undefined;
	if (getUserRole() === 'admin') {
		return getAppToken();
	}

	accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
	if (!accessToken) {
		return undefined;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		return accessToken;
	}

	return undefined;
}

export function getUserRole(): undefined | string {
	const role = localStorage.getItem(USER_ROLE);
	if (!role) {
		return undefined;
	}

	return role;
}

export function isNumeric(num: any) {
	return !isNaN(num);
}

export type NavbarVisibility = 'visible' | 'hidden';

export const GET_ISSUE_BY_ID_QUERY = `
	query Issue($id: ID!) {
		issue(id: $id) {
			id
			name
			description
			textDirection
			externalUrl
			pages
			cover
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
			articles {
				id
				ArticleID
				thumbnail
				author
				style
				name
				role
				slug
				keywords
				audios {
	  	    id
					url
				  lang
				}
				channel
				index
				description
				pages
				components
				styleProperties
				style
				updated
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

// TODO Revert back to get features
export const HACK_GET_ISSUE_BY_ID_QUERY = `
	query Issue($id: ID!) {
		issue(id: $id) {
			id
			name
			description
			textDirection
			externalUrl
			pages
			cover
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
			articles {
				id
				ArticleID
				thumbnail
				style
				name
				role
				slug
				keywords
				audio
				channel
				index
				description
				pages
				components
				styleProperties
				style
				updated
				isFeatured
				replica {
					name
					page
					image
					hotspots {
						url
						area {
							x
							y
							width
							height
						}
					}
				}
			}
		}
	}
`;

export const LANGUAGES: any = {
	en: {
		en: 'English',
		es: 'Spanish',
		fr: 'French',
		de: 'German',
		ja: 'Japanese',
		zh: 'Chinese',
		nl: 'Dutch',
	},
	es: {
		en: 'Ingles',
		es: 'Español',
		fr: 'Frances',
		de: 'Aleman',
		ja: 'Japones',
		zh: 'Chino',
		nl: 'Holandés',
	},
	fr: {
		en: 'Anglais',
		es: 'Espagnol',
		fr: 'Français',
		de: 'Allemand',
		ja: 'Japonais',
		zh: 'Chinoise',
		nl: 'Néerlandaise',
	},
	de: {
		en: 'Englisch',
		es: 'Spanisch',
		fr: 'Französisch',
		de: 'Deutsch',
		ja: 'Japanisch',
		zh: 'Chinesisch',
		nl: 'Niederländische',
	},
	ja: {
		en: '英語',
		es: 'スペイン語',
		fr: 'フランス語',
		de: 'ドイツ語',
		ja: '日本語',
		zh: '中国語',
		nl: 'オランダ人',
	},
	zh: {
		en: '英语',
		es: '西班牙语',
		fr: '法语',
		de: '德语',
		ja: '日本人',
		zh: '中国人',
		nl: '荷兰语',
	},
};

// TOOD This functions is only for hacking features
export function mapIssuesNonImplementedFeatures(issue: Issue): Issue {
	issue = clone(issue);
	if (!issue.features) {
		issue.features = [];
	}

	if (issue.articles) {
		issue.articles = issue.articles.map(mapArticlesNonImplementedFeatures);
	}

	if (issue.featuredArticles) {
		issue.featuredArticles = issue.featuredArticles.map(
			mapArticlesNonImplementedFeatures,
		);
	}

	return issue;
}

// TOOD This functions is only for hacking features
export function mapArticlesNonImplementedFeatures(article: Article): Article {
	article = clone(article);
	if (!article.features) {
		article.features = [];
	}

	return article;
}
